import { FC, KeyboardEvent } from 'react'
import { UseFormRegister, UseFormGetValues, FieldErrors } from 'react-hook-form'
import { useRouter } from 'next/router'
import { WishlistManager } from '../../Hooks/WishlistActionTypes'
import { Wishlist, WishlistItemUpdateInput } from '@pylot-data/pylotschema'
import { LoadingDots } from '@corratech/pylot-ui'
import { PWALogo } from '@components/icons'
import s from '@pagestyles/Wishlist.module.scss'
import styles from './WishlistCollection.module.scss'
import useIsMobile from '@lib/hooks/useIsMobile'

export type wishlist = {
    wishlist: Wishlist
    wishlistManager: WishlistManager
    register: UseFormRegister<Array<WishlistItemUpdateInput>>
    getValues?: UseFormGetValues<Array<WishlistItemUpdateInput>>
    errors: FieldErrors<Array<WishlistItemUpdateInput>>
}

type WishlistCardProps = {
    collectionIndex: number
    title: string
    images: string[]
    wishlistId: string
    handleSelectWishlist?: (id: string) => void
    selectedWishlist?: string
    isAddtoWishlistLoading?: boolean
}

const capitalizeFirstLetter = (name?: string) => {
    if (!name) return ''
    return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase()
}

const getDimension = (collectionIndex: number, index: number) => {
    const baseStyle = { maxWidth: '100%', margin: 'auto' }

    switch (collectionIndex) {
        case 4:
            return { ...baseStyle, width: '50%', height: '50%' }
        case 2:
            return { ...baseStyle, width: '50%', height: '100%' }
        case 3:
            if (index < 2) {
                return { ...baseStyle, width: '50%' }
            } else {
                return { ...baseStyle, width: '50%' }
            }
        case 1:
            return { ...baseStyle, width: '100%', height: '100%' }
        default:
            return { ...baseStyle, width: '100%', height: 'auto' }
    }
}

const DEFAULT_NAME = 'Lista de deseos'
const DEFAULT_NAME_REPLACEMENT = 'Favoritos guardados'

export const WishlistCard: FC<WishlistCardProps> = ({
    collectionIndex,
    title,
    images,
    wishlistId,
    handleSelectWishlist,
    selectedWishlist,
    isAddtoWishlistLoading
}) => {
    const router = useRouter()
    const isMobile = useIsMobile()

    const handleGoToWishlist = () => {
        void router.push(`/account/wishlist/${wishlistId}`)
    }

    const activateCard = () => {
        if (handleSelectWishlist) {
            handleSelectWishlist(wishlistId)
        } else {
            handleGoToWishlist()
        }
    }

    const handleCardKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            activateCard()
        }
    }

    const isSelectedWishlist = selectedWishlist === wishlistId

    const isAddLoading = isAddtoWishlistLoading && isSelectedWishlist

    const imagesRender = images.length ? (
        images.map((image, index) => (
            <div
                className={styles['collection-images']}
                key={index}
                style={getDimension(collectionIndex, index)}
            >
                <img src={image} alt={`Imagen ${index + 1}`} />
            </div>
        ))
    ) : (
        <div className={styles['collection-image']}>
            <PWALogo />
        </div>
    )

    const loader = (
        <div className={styles['collection-loader']}>
            <LoadingDots />
        </div>
    )

    const isDefaultListName = title === DEFAULT_NAME

    const titleRender = isDefaultListName
        ? DEFAULT_NAME_REPLACEMENT
        : capitalizeFirstLetter(title)

    return (
        <div className={styles['collection-content']}>
            <div
                className={styles['collection-card-hit']}
                role="button"
                tabIndex={0}
                aria-label={titleRender}
                onClick={activateCard}
                onKeyDown={handleCardKeyDown}
            >
                <div
                    className={styles['collection']}
                    style={{
                        filter:
                            isSelectedWishlist && !isMobile
                                ? 'brightness(0.7)'
                                : 'none'
                    }}
                >
                    {isAddLoading && loader}
                    {imagesRender}
                </div>
                <h5 className={styles['collection-title']}>{titleRender}</h5>
            </div>
        </div>
    )
}
